import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";

interface SessionInfo {
  token: string;
  targetSiteId: number;
  targetSiteName: string;
  expiresAt: string;
}

function getSession(): SessionInfo | null {
  try {
    const raw = sessionStorage.getItem("supportSession");
    if (!raw) return null;
    const s = JSON.parse(raw) as SessionInfo;
    if (new Date(s.expiresAt) <= new Date()) {
      sessionStorage.removeItem("supportSession");
      sessionStorage.removeItem("supportSessionToken");
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export default function SupportSessionBanner() {
  const api = useApi();
  const [session, setSession] = useState<SessionInfo | null>(getSession);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    const tick = () => {
      const s = getSession();
      setSession(s);
      if (s) {
        setSecondsLeft(Math.max(0, Math.round((new Date(s.expiresAt).getTime() - Date.now()) / 1000)));
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  if (!session) return null;

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  async function revoke() {
    if (!session) return;
    setRevoking(true);
    try {
      await api.admin.delete(`SupportTools/sessions/${session.token}`);
    } catch { /* already expired */ }
    sessionStorage.removeItem("supportSession");
    sessionStorage.removeItem("supportSessionToken");
    setSession(null);
    setRevoking(false);
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white flex items-center justify-between px-4 py-2 text-sm font-medium shadow-lg">
      <span>
        Viewing as <strong>{session.targetSiteName}</strong> (site {session.targetSiteId}) — support session active
      </span>
      <div className="flex items-center gap-4">
        <span className="font-mono text-amber-100">
          {secondsLeft > 0 ? `Expires in ${mm}:${ss}` : "Expired"}
        </span>
        <button
          onClick={revoke}
          disabled={revoking}
          className="bg-white text-amber-700 rounded px-3 py-1 text-xs font-semibold hover:bg-amber-100 disabled:opacity-50"
        >
          {revoking ? "Ending…" : "End Session"}
        </button>
      </div>
    </div>
  );
}

export function startSupportSession(info: Omit<SessionInfo, "token"> & { token: string }) {
  sessionStorage.setItem("supportSessionToken", info.token);
  sessionStorage.setItem("supportSession", JSON.stringify(info));
  window.dispatchEvent(new Event("supportSessionChanged"));
}

export function clearSupportSession() {
  sessionStorage.removeItem("supportSessionToken");
  sessionStorage.removeItem("supportSession");
  window.dispatchEvent(new Event("supportSessionChanged"));
}
